/**
 * How the agent opens the call (S05's salutation, spoken).
 *
 * The preset carries a hardcoded first message — "Здравейте, господин Иванов!" —
 * which greeted every contact by one demo contact's surname. Removing the
 * contact block from the agent's *instructions* did not fix it, because the
 * greeting is a separate field spoken verbatim before any reasoning happens.
 * So the console sends the opening line per call, which is where a personalised
 * greeting belongs.
 *
 * ## Written form is not spoken form
 *
 * The instruction context uses `г-н`, which a model reads fine. A greeting is
 * fed to TTS, and an abbreviation is a gamble on how it gets pronounced. So the
 * spoken forms are spelled out: господин, госпожа, доктор, професор.
 *
 * ## Which part of the name
 *
 * With a salutation, Bulgarian convention on a cold call is honorific plus
 * surname — "господин Геновски". Without one, the first name alone — "Михаил" —
 * which reads as friendly rather than as a form with a field missing. Prefixing
 * nothing to a full name would sound like a database record being read out.
 */
import type { Salutation } from './enums.js';

/**
 * The default opening line, and the fallback if the setting is missing.
 *
 * Two placeholders. `{contact}` is whatever the greeting rule produced, already
 * correct for this contact. `{agent}` is the agent's own name, and it is a
 * placeholder for a reason worth spelling out (C5).
 *
 * ## Why the agent's name is not written here
 *
 * The platform agent has a `name` field — "the LLM-facing agent name known to
 * the AI" — which reads Ева. This template used to say Елена. Both were true of
 * something and neither was true of the other, and the one an operator would
 * naturally go and edit, in the agent editor, was the one with no effect on what
 * a contact hears.
 *
 * That is the same shape of bug as the greeting that called everybody Иванов:
 * a field that looks authoritative, one layer removed from the thing that
 * actually speaks. So the name now comes from the agent, once, and editing it in
 * S10's editor changes the call.
 */
export const DEFAULT_GREETING_TEMPLATE =
  'Здравейте, {contact}! Аз съм {agent}, дигитален асистент от Планет Шварц, зала за събития. Обаждам се съвсем накратко — имате ли минутка?';

/** Spoken honorifics. Deliberately spelled out — this goes to TTS. */
const SPOKEN_HONORIFIC: Record<Salutation, string> = {
  MR: 'господин',
  MS: 'госпожа',
  MRS: 'госпожа',
  // No settled spoken Bulgarian honorific, and inventing one on a sales call is
  // worse than warmth: MX falls through to the first name like NONE.
  MX: '',
  DR: 'доктор',
  PROF: 'професор',
  NONE: '',
};

/**
 * How to address this contact out loud.
 *
 * Splitting a name is a guess, so the guess is made where it is cheapest to be
 * wrong: a single-word name is used whole in both branches, and a name with more
 * parts takes the first word as the given name and the last as the family name.
 * That is right for "Михаил Геновски" and for "Мария Иванова Петрова"; it is
 * wrong for a culture that orders names the other way, which is worth knowing
 * before this ships anywhere but Bulgaria.
 */
export function spokenAddress(name: string, salutation: Salutation): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';

  const first = parts[0] ?? '';
  const last = parts[parts.length - 1] ?? '';
  const honorific = SPOKEN_HONORIFIC[salutation];

  // No salutation means no honorific to attach, and "Здравейте, Михаил" is
  // warmer than a bare surname — which without a title sounds like a summons.
  if (!honorific) return first;

  return `${honorific} ${last}`;
}

/**
 * Fill the template. Unknown placeholders are left alone rather than blanked.
 *
 * `agentName` is null before the first agent sync, and null is why `{agent}` is
 * left in place rather than substituted with something: an empty string would
 * have the agent say "Аз съм , дигитален асистент", which is worse out loud than
 * an unresolved placeholder is in a log. In practice the sync runs at startup, so
 * a null here means the platform was unreachable — and that is worth hearing
 * about rather than papering over with a hardcoded name, which is the habit this
 * placeholder exists to break.
 */
export function buildGreeting(
  name: string,
  salutation: Salutation,
  template: string = DEFAULT_GREETING_TEMPLATE,
  agentName: string | null = null,
): string {
  const spoken = spokenAddress(name, salutation);
  // `{address}` was this placeholder's first name and is still honoured. Not for
  // tidiness: a stored template that missed the rename would otherwise be read
  // aloud with the literal braces in it, and a greeting is the one setting a
  // contact hears before anybody here notices.
  const filled = template.replaceAll('{contact}', spoken).replaceAll('{address}', spoken);

  return agentName === null || agentName.trim() === ''
    ? filled
    : filled.replaceAll('{agent}', agentName.trim());
}

/**
 * The placeholders a greeting may carry, for the editor to offer and explain.
 *
 * Exported as data rather than duplicated in the UI: a chip an operator can
 * click to insert `{agent}` and a `replaceAll('{agent}', …)` in `buildGreeting`
 * have to agree on the exact spelling, and one of those is easy to mistype.
 */
export const GREETING_PLACEHOLDERS = [
  {
    token: '{contact}',
    label: 'Contact',
    describes: 'Honorific and surname, or the first name when no salutation is set.',
  },
  {
    token: '{agent}',
    label: 'Agent name',
    describes: "The agent's own name, taken from the field above.",
  },
] as const;

/** One thing worth telling the operator about a greeting they have written. */
export interface GreetingWarning {
  token: string;
  message: string;
}

/**
 * What is worth saying about a template — advisory, never blocking.
 *
 * Both of these are legitimate choices. A campaign might genuinely want an
 * impersonal opening, and an operator might deliberately write one agent's name
 * inline. But both are also easy to do *by accident* while editing a sentence,
 * and the consequence is invisible until somebody listens to a call — so they are
 * surfaced here and enforced nowhere.
 *
 * The `{agent}` case is the one this exists for. Typing "Ева" where the
 * placeholder was recreates exactly the split that made the agent's name field
 * and the spoken greeting disagree: the console would show a name field that no
 * longer changes anything.
 */
export function greetingWarnings(template: string): GreetingWarning[] {
  const warnings: GreetingWarning[] = [];

  // Either spelling counts as present: the legacy one still fills, so warning
  // about it would be warning about something that works.
  if (!template.includes('{contact}') && !template.includes('{address}')) {
    warnings.push({
      token: '{contact}',
      message: 'Without {contact} every contact is greeted identically — no name and no honorific.',
    });
  }

  if (!template.includes('{agent}')) {
    warnings.push({
      token: '{agent}',
      message:
        'Without {agent} the greeting no longer follows the agent name above, so editing that field will not change what callers hear.',
    });
  }

  return warnings;
}
